import { useMyWeek } from "hooks/useCloseTracker";

/**
 * The sidebar badge for the close tracker (R1.5): closes awaiting this user's
 * sign-off, plus their waiting items whose follow-up date has arrived.
 *
 * The server already computes exactly that number in `close/my-week`, so this
 * reads it rather than recounting. Two implementations of "how much work does
 * this person have today" is how a badge ends up disagreeing with the page it
 * points at.
 *
 * `enabled` is false until the `workflows_v2` flag is on, and a disabled hook
 * passes SWR a null key — so a firm without the feature issues no request at
 * all, rather than one 404 or one wasted round trip per page load.
 */
export function useCloseTrackerBadge(enabled: boolean): number {
	const { myWeek, error } = useMyWeek(enabled);
	if (!enabled) return 0;
	// 🪤 KNOWN GAP, stated rather than hidden: on a failed fetch the count is
	// UNKNOWN, but this hook returns a number and AntD's Badge hides 0 — so a
	// reviewer with sign-offs waiting sees a clean sidebar. Fixing it properly
	// means the badge carrying a third state (unknown) that the menu can render,
	// which is a change to `AppMenu`, not to this hook. Until then the failure is
	// at least logged rather than silently treated as good news.
	if (error) {
		console.error("Close tracker badge could not be loaded", error);
		return 0;
	}
	return myWeek?.badge ?? 0;
}
